import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      // Navigation
      'nav.brand': 'Games Hub',
      'nav.home': 'Home',
      'nav.games': 'Games',
      'nav.about': 'About',
      'nav.contact': 'Contact',
      'consent_title': 'Cookies & analytics',
      'consent_text': 'We use necessary cookies to run this site. Analytics are optional and only load after you opt in.',
      'consent_analytics': 'Allow analytics (Google Analytics)',
      'consent_accept': 'Accept all',
      'consent_necessary': 'Necessary only',
      'consent_manage': 'Manage',
      'consent_save': 'Save preferences',
      'footer_note': 'game.aixcalculator.com · Free browser games',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Experience the Future of Gaming',
      'hero.description': 'Immerse yourself in a collection of AI-powered puzzle games with cutting-edge cyber aesthetics',
      'hero.cta': 'Start Playing',
      
      // Hero Features
      'hero.features.ai.title': 'AI Powered',
      'hero.features.ai.description': 'Advanced algorithms for challenging gameplay',
      'hero.features.games.title': 'Multiple Games',
      'hero.features.games.description': 'Collection of diverse puzzle challenges',
      'hero.features.aesthetics.title': 'Cyber Aesthetics',
      'hero.features.aesthetics.description': 'Futuristic design and visual effects',
      
      // Games
      'games.continue': 'Continue playing',
      'daily.title': 'Daily Challenge',
      'daily.subtitle': 'One featured game picked for today — same for everyone.',
      'moreTools': 'More tools',
      'progress.score': 'Best {{score}}',
      'progress.plays': '{{count}} plays',
      'achievements.title': 'Achievements',
      'achievements.first_play': 'First game',
      'achievements.played_3': '3 games played',
      'achievements.played_5': '5 games played',
      'achievements.played_10': '10 games played',
      'achievements.streak_3': '3-day streak',
      'games.filter.all': 'All',
      'games.filter.puzzle': 'Puzzle',
      'games.filter.arcade': 'Arcade',
      'games.filter.brain': 'Brain',
      'games.title': 'Game Collection',
      'games.subtitle': 'Choose Your Challenge',
      'games.memory': 'Memory Matrix',
      'games.puzzle': 'Cyber Puzzle',
      'games.logic': 'Logic Gates',
      'games.pattern': 'Pattern Master',
      'games.speed': 'Speed Test',
      'games.dino': 'Chrome Dino Runner',
      'games.snake': 'Cyber Snake',
      'games.tetris': 'Cyber Tetris',
      'games.game2048': '2048',
      'games.colormatch': 'Color Match',
      'games.wordsearch': 'Word Search',
      'games.math': 'Math Challenge',
      'games.reaction': 'Reaction Test',
      'games.rhythm': 'Rhythm Master',
      'games.maze': 'Cyber Maze',
      
      // Game Descriptions
      'games.memory.desc': 'Test your memory with AI-generated patterns',
      'games.puzzle.desc': 'Solve cyber-themed puzzles',
      'games.logic.desc': 'Master digital logic circuits',
      'games.pattern.desc': 'Discover hidden patterns',
      'games.speed.desc': 'Challenge your reaction time',
      'games.dino.desc': 'Classic offline dinosaur game with cyber aesthetics',
      'games.snake.desc': 'Classic snake game with neon visuals and growing challenges',
      'games.tetris.desc': 'Futuristic tetris with neon blocks and increasing speed',
      'games.game2048.desc': 'Addictive number puzzle game with cyber styling',
      'games.colormatch.desc': 'Test your color recognition and matching skills',
      'games.wordsearch.desc': 'Find hidden words in a cyber-themed grid',
      'games.math.desc': 'Solve math problems and build streaks for bonus points',
      'games.reaction.desc': 'Test your reflexes and reaction time',
      'games.rhythm.desc': 'Click notes in rhythm with the beat and build combos',
      'games.maze.desc': 'Navigate through procedurally generated mazes',
      
      // Games CTA
      'games.cta.description': 'Ready to challenge your mind?',
      'games.cta.button': 'Explore All Games',
      
      // Game UI
      'game.score': 'Score',
      'game.level': 'Level',
      'game.time': 'Time',
      'game.start': 'Start',
      'game.pause': 'Pause',
      'game.restart': 'Restart',
      'game.next': 'Next Level',
      'game.play': 'Play',
      'game.backToGames': 'Back to Games',
      
      // Game Page
      'game.notFound': 'Game Not Found',
      'game.available': 'Available games',
      'game.backToHome': 'Back to Home',
      'game.loading': 'Loading',
      'game.game': 'Game',
      
      // Memory Game
      'memory.reset': 'Reset',
      'memory.title': 'Memory Matrix Challenge',
      'memory.instructions': 'Find matching pairs of symbols. Remember their positions and match them all to advance to the next level!',
      'memory.startGame': 'Start Game',
      'memory.levelProgress': 'Level Progress',
      'memory.pairsFound': 'pairs found',
      
      // Puzzle Game
      'puzzle.reset': 'Reset',
      'puzzle.title': 'Cyber Puzzle Challenge',
      'puzzle.instructions': 'Arrange the numbers in order from 1 to {count} with the empty space at the bottom right. Click on tiles adjacent to the empty space to move them.',
      'puzzle.startGame': 'Start Game',
      'puzzle.completed': 'Puzzle Completed!',
      'puzzle.score': 'Score',
      'puzzle.moves': 'Moves',
      'puzzle.time': 'Time',
      'puzzle.nextLevel': 'Next Level',
      'puzzle.playAgain': 'Play Again',
      'puzzle.gameStats': 'Game Statistics',
      'puzzle.currentScore': 'Current Score',
      'puzzle.efficiency': 'Efficiency',
      'puzzle.timeBonus': 'Time Bonus',
      
      // Footer
      'footer.brand': 'Games Hub',
      'footer.copyright': '© 2025-∞ Games Hub. All rights reserved.',
      'footer.made': 'Made with ❤️ and AI',
      'footer.quickLinks': 'Quick Links',
      'footer.games': 'Games',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Experience the future of gaming with our AI-powered puzzle collection. Challenge your mind with cutting-edge cyber aesthetics and innovative gameplay.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Easy',
      'game.difficulty.medium': 'Medium',
      'game.difficulty.hard': 'Hard',
      
      // Mobile Language Selector
      'language.selector': 'Language:',
    }
  },
  zh: {
    translation: {
      // Navigation
      'nav.brand': 'Games Hub',
      'nav.home': '首页',
      'nav.games': '游戏',
      'nav.about': '关于',
      'nav.contact': '联系',
      'consent_title': 'Cookie 与统计分析',
      'consent_text': '本站使用必要 Cookie。统计分析为可选项，仅在你同意后加载。',
      'consent_analytics': '允许统计分析（Google Analytics）',
      'consent_accept': '全部接受',
      'consent_necessary': '仅必要',
      'consent_manage': '管理',
      'consent_save': '保存偏好',
      'footer_note': 'game.aixcalculator.com · 免费浏览器游戏',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': '体验游戏未来',
      'hero.description': '沉浸在AI驱动的益智游戏集合中，体验前沿的赛博美学',
      'hero.cta': '开始游戏',
      
      // Hero Features
      'hero.features.ai.title': 'AI驱动',
      'hero.features.ai.description': '先进算法提供挑战性游戏体验',
      'hero.features.games.title': '多款游戏',
      'hero.features.games.description': '多样化的益智挑战集合',
      'hero.features.aesthetics.title': '赛博美学',
      'hero.features.aesthetics.description': '未来科技设计与视觉效果',
      
      // Games
      'games.continue': '继续游戏',
      'daily.title': '每日挑战',
      'daily.subtitle': '按日期推荐一款游戏，今日全员相同。',
      'moreTools': '更多工具',
      'progress.score': '最高 {{score}} 分',
      'progress.plays': '已玩 {{count}} 次',
      'achievements.title': '成就徽章',
      'achievements.first_play': '首次游玩',
      'achievements.played_3': '玩过 3 款',
      'achievements.played_5': '玩过 5 款',
      'achievements.played_10': '玩过 10 款',
      'achievements.streak_3': '连续 3 天',
      'games.filter.all': '全部',
      'games.filter.puzzle': '益智',
      'games.filter.arcade': '街机',
      'games.filter.brain': '脑力',
      'games.title': '游戏集合',
      'games.subtitle': '选择你的挑战',
      'games.memory': '记忆矩阵',
      'games.puzzle': '赛博拼图',
      'games.logic': '逻辑门',
      'games.pattern': '模式大师',
      'games.speed': '速度测试',
      'games.dino': 'Chrome恐龙跑酷',
      'games.snake': '赛博蛇',
      'games.tetris': '赛博俄罗斯方块',
      'games.game2048': '2048',
      'games.colormatch': '颜色匹配',
      'games.wordsearch': '单词搜索',
      'games.math': '数学挑战',
      'games.reaction': '反应测试',
      'games.rhythm': '节奏大师',
      'games.maze': '赛博迷宫',
      
      // Game Descriptions
      'games.memory.desc': '用AI生成的模式测试你的记忆力',
      'games.puzzle.desc': '解决赛博主题拼图',
      'games.logic.desc': '掌握数字逻辑电路',
      'games.pattern.desc': '发现隐藏模式',
      'games.speed.desc': '挑战你的反应时间',
      'games.dino.desc': '经典离线恐龙游戏，具有赛博美学',
      'games.snake.desc': '经典贪吃蛇游戏，具有霓虹视觉效果和不断增长的挑战',
      'games.tetris.desc': '未来俄罗斯方块，具有霓虹方块和不断增加的速度',
      'games.game2048.desc': '令人上瘾的数字益智游戏，具有赛博风格',
      'games.colormatch.desc': '测试你的颜色识别和匹配技能',
      'games.wordsearch.desc': '在赛博主题网格中寻找隐藏单词',
      'games.math.desc': '解决数学问题并建立连胜以获得奖励分数',
      'games.reaction.desc': '测试你的反应能力和反应时间',
      'games.rhythm.desc': '跟随节拍点击音符并建立连击',
      'games.maze.desc': '在程序生成的迷宫中导航',
      
      // Games CTA
      'games.cta.description': '准备好挑战你的思维了吗？',
      'games.cta.button': '探索所有游戏',
      
      // Game UI
      'game.score': '分数',
      'game.level': '等级',
      'game.time': '时间',
      'game.start': '开始',
      'game.pause': '暂停',
      'game.restart': '重新开始',
      'game.next': '下一关',
      'game.play': '开始',
      'game.backToGames': '返回游戏',
      
      // Game Page
      'game.notFound': '游戏未找到',
      'game.available': '可用的游戏',
      'game.backToHome': '返回首页',
      'game.loading': '加载中',
      'game.game': '游戏',
      
      // Memory Game
      'memory.reset': '重置',
      'memory.title': '记忆矩阵挑战',
      'memory.instructions': '找到匹配的符号对。记住它们的位置，全部匹配以进入下一关！',
      'memory.startGame': '开始游戏',
      'memory.levelProgress': '关卡进度',
      'memory.pairsFound': '找到的配对',
      
      // Puzzle Game
      'puzzle.reset': '重置',
      'puzzle.title': '赛博拼图挑战',
      'puzzle.instructions': '将数字从1到{count}按顺序排列，空格在右下角。点击空白格相邻的方块移动它们。',
      'puzzle.startGame': '开始游戏',
      'puzzle.completed': '拼图完成！',
      'puzzle.score': '分数',
      'puzzle.moves': '移动次数',
      'puzzle.time': '时间',
      'puzzle.nextLevel': '下一关',
      'puzzle.playAgain': '再玩一次',
      'puzzle.gameStats': '游戏统计',
      'puzzle.currentScore': '当前分数',
      'puzzle.efficiency': '效率',
      'puzzle.timeBonus': '时间奖励',
      
      // Footer
      'footer.brand': 'Games Hub',
      'footer.copyright': '© 2025-∞ Games Hub。保留所有权利。',
      'footer.made': '用 ❤️ 和AI制作',
      'footer.quickLinks': '快速链接',
      'footer.games': '游戏',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      
      // Footer Description
      'footer.description': '体验我们AI驱动的益智游戏集合的游戏未来。以前沿的赛博美学和创新游戏玩法挑战你的思维。',
      
      // Game Difficulty
      'game.difficulty.easy': '简单',
      'game.difficulty.medium': '中等',
      'game.difficulty.hard': '困难',
      
      // Mobile Language Selector
      'language.selector': '语言：',
    }
  },
  ko: {
    translation: {
      // Navigation
      'nav.home': '홈',
      'nav.games': '게임',
      'nav.about': '소개',
      'nav.contact': '연락처',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': '게임의 미래를 경험하세요',
      'hero.description': '최첨단 사이버 미학을 갖춘 AI 기반 퍼즐 게임 컬렉션에 몰입하세요',
      'hero.cta': '게임 시작',
      
      // Games
      'games.title': '게임 컬렉션',
      'games.subtitle': '도전을 선택하세요',
      'games.memory': '메모리 매트릭스',
      'games.puzzle': '사이버 퍼즐',
      'games.logic': '로직 게이트',
      'games.pattern': '패턴 마스터',
      'games.speed': '스피드 테스트',
      
      // Game Descriptions
      'games.memory.desc': 'AI 생성 패턴으로 기억력 테스트',
      'games.puzzle.desc': '사이버 테마 퍼즐 해결',
      'games.logic.desc': '디지털 로직 회로 마스터',
      'games.pattern.desc': '숨겨진 패턴 발견',
      'games.speed.desc': '반응 시간에 도전',
      
      // Game UI
      'game.score': '점수',
      'game.level': '레벨',
      'game.time': '시간',
      'game.start': '시작',
      'game.pause': '일시정지',
      'game.restart': '재시작',
      'game.next': '다음 레벨',
      
      // Game Page
      'game.notFound': '게임을 찾을 수 없음',
      'game.available': '사용 가능한 게임',
      'game.backToHome': '홈으로 돌아가기',
      'game.loading': '로딩 중',
      'game.game': '게임',
      
      // Memory Game
      'memory.reset': '재설정',
      'memory.title': '메모리 매트릭스 도전',
      'memory.instructions': '일치하는 기호 쌍을 찾으세요. 위치를 기억하고 모두 일치시켜 다음 레벨로 진행하세요!',
      'memory.startGame': '게임 시작',
      'memory.levelProgress': '레벨 진행률',
      'memory.pairsFound': '쌍을 찾았습니다',
      
      // Puzzle Game
      'puzzle.reset': '재설정',
      'puzzle.title': '사이버 퍼즐 도전',
      'puzzle.instructions': '1부터 {count}까지의 숫자를 빈 공간이 우측 하단에 있도록 순서대로 배열하세요. 빈 공간 옆의 타일을 클릭하여 이동하세요.',
      'puzzle.startGame': '게임 시작',
      'puzzle.completed': '퍼즐 완료!',
      'puzzle.score': '점수',
      'puzzle.moves': '이동 횟수',
      'puzzle.time': '시간',
      'puzzle.nextLevel': '다음 레벨',
      'puzzle.playAgain': '다시 플레이',
      'puzzle.gameStats': '게임 통계',
      'puzzle.currentScore': '현재 점수',
      'puzzle.efficiency': '효율성',
      'puzzle.timeBonus': '시간 보너스',
      
      // Footer
      'footer.copyright': '© 2025-∞ AI 게임 허브. 모든 권리 보유.',
      'footer.made': '❤️ 와 AI로 제작',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'AI 기반 퍼즐 컬렉션으로 게임의 미래를 경험하세요. 최첨단 사이버 미학과 혁신적인 게임플레이로 마음을 도전하세요.',
      
      // Game Difficulty
      'game.difficulty.easy': '쉬움',
      'game.difficulty.medium': '보통',
      'game.difficulty.hard': '어려움',
      
      // Mobile Language Selector
      'language.selector': '언어:',
    }
  },
  fr: {
    translation: {
      // Navigation
      'nav.home': 'Accueil',
      'nav.games': 'Jeux',
      'nav.about': 'À propos',
      'nav.contact': 'Contact',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Vivez l\'Avenir du Jeu',
      'hero.description': 'Plongez dans une collection de jeux de puzzle alimentés par l\'IA avec une esthétique cyber de pointe',
      'hero.cta': 'Commencer à Jouer',
      
      // Games
      'games.title': 'Collection de Jeux',
      'games.subtitle': 'Choisissez Votre Défi',
      'games.memory': 'Matrice de Mémoire',
      'games.puzzle': 'Puzzle Cyber',
      'games.logic': 'Portes Logiques',
      'games.pattern': 'Maître des Motifs',
      'games.speed': 'Test de Vitesse',
      
      // Game Descriptions
      'games.memory.desc': 'Testez votre mémoire avec des motifs générés par l\'IA',
      'games.puzzle.desc': 'Résolvez des puzzles à thème cyber',
      'games.logic.desc': 'Maîtrisez les circuits logiques numériques',
      'games.pattern.desc': 'Découvrez des motifs cachés',
      'games.speed.desc': 'Défiez votre temps de réaction',
      
      // Game UI
      'game.score': 'Score',
      'game.level': 'Niveau',
      'game.time': 'Temps',
      'game.start': 'Commencer',
      'game.pause': 'Pause',
      'game.restart': 'Recommencer',
      'game.next': 'Niveau Suivant',
      
      // Game Page
      'game.notFound': 'Jeu Non Trouvé',
      'game.available': 'Jeux Disponibles',
      'game.backToHome': 'Retour à l\'Accueil',
      'game.loading': 'Chargement',
      'game.game': 'Jeu',
      
      // Memory Game
      'memory.reset': 'Réinitialiser',
      'memory.title': 'Défi Matrice de Mémoire',
      'memory.instructions': 'Trouvez des paires de symboles correspondants. Mémorisez leurs positions et associez-les tous pour passer au niveau suivant !',
      'memory.startGame': 'Commencer le Jeu',
      'memory.levelProgress': 'Progression du Niveau',
      'memory.pairsFound': 'paires trouvées',
      
      // Puzzle Game
      'puzzle.reset': 'Réinitialiser',
      'puzzle.title': 'Défi Puzzle Cyber',
      'puzzle.instructions': 'Arrangez les nombres de 1 à {count} dans l\'ordre, avec l\'espace vide en bas à droite. Cliquez sur les tuiles adjacentes à l\'espace vide pour les déplacer.',
      'puzzle.startGame': 'Commencer le Jeu',
      'puzzle.completed': 'Puzzle Complet !',
      'puzzle.score': 'Score',
      'puzzle.moves': 'Mouvements',
      'puzzle.time': 'Temps',
      'puzzle.nextLevel': 'Niveau Suivant',
      'puzzle.playAgain': 'Rejouer',
      'puzzle.gameStats': 'Statistiques du Jeu',
      'puzzle.currentScore': 'Score Actuel',
      'puzzle.efficiency': 'Efficacité',
      'puzzle.timeBonus': 'Bonus Temps',
      
      // Footer
      'footer.copyright': '© 2025-∞ Hub de Jeux IA. Tous droits réservés.',
      'footer.made': 'Fait avec ❤️ et IA',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Vivez l\'avenir du jeu avec notre collection de puzzles alimentés par l\'IA. Défiez votre esprit avec une esthétique cyber de pointe et un gameplay innovant.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Facile',
      'game.difficulty.medium': 'Moyen',
      'game.difficulty.hard': 'Difficile',
      
      // Mobile Language Selector
      'language.selector': 'Langue :',
    }
  },
  de: {
    translation: {
      // Navigation
      'nav.home': 'Startseite',
      'nav.games': 'Spiele',
      'nav.about': 'Über uns',
      'nav.contact': 'Kontakt',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Erleben Sie die Zukunft des Spielens',
      'hero.description': 'Tauchen Sie ein in eine Sammlung von KI-gestützten Puzzle-Spielen mit futuristischer Cyber-Ästhetik',
      'hero.cta': 'Spiel Starten',
      
      // Games
      'games.title': 'Spiele-Sammlung',
      'games.subtitle': 'Wählen Sie Ihre Herausforderung',
      'games.memory': 'Gedächtnis-Matrix',
      'games.puzzle': 'Cyber-Puzzle',
      'games.logic': 'Logik-Gatter',
      'games.pattern': 'Muster-Meister',
      'games.speed': 'Geschwindigkeitstest',
      
      // Game Descriptions
      'games.memory.desc': 'Testen Sie Ihr Gedächtnis mit KI-generierten Mustern',
      'games.puzzle.desc': 'Lösen Sie Cyber-Themen-Puzzles',
      'games.logic.desc': 'Meistern Sie digitale Logik-Schaltkreise',
      'games.pattern.desc': 'Entdecken Sie versteckte Muster',
      'games.speed.desc': 'Fordern Sie Ihre Reaktionszeit heraus',
      
      // Game UI
      'game.score': 'Punktzahl',
      'game.level': 'Level',
      'game.time': 'Zeit',
      'game.start': 'Start',
      'game.pause': 'Pause',
      'game.restart': 'Neustart',
      'game.next': 'Nächstes Level',
      
      // Game Page
      'game.notFound': 'Spiel Nicht Gefunden',
      'game.available': 'Spiele Verfügbar',
      'game.backToHome': 'Zurück zur Startseite',
      'game.loading': 'Lädt',
      'game.game': 'Spiel',
      
      // Memory Game
      'memory.reset': 'Zurücksetzen',
      'memory.title': 'Gedächtnis-Matrix-Herausforderung',
      'memory.instructions': 'Finden Sie passende Symbolpaare. Merken Sie sich ihre Positionen und verbinden Sie alle, um zum nächsten Level zu gelangen!',
      'memory.startGame': 'Spiel Starten',
      'memory.levelProgress': 'Level-Fortschritt',
      'memory.pairsFound': 'Paare gefunden',
      
      // Puzzle Game
      'puzzle.reset': 'Zurücksetzen',
      'puzzle.title': 'Cyber-Puzzle-Herausforderung',
      'puzzle.instructions': 'Setzen Sie die Zahlen von 1 bis {count} in der richtigen Reihenfolge, mit dem leeren Feld in der unteren rechten Ecke. Klicken Sie auf die Felder neben dem leeren Feld, um sie zu bewegen.',
      'puzzle.startGame': 'Spiel Starten',
      'puzzle.completed': 'Puzzle abgeschlossen!',
      'puzzle.score': 'Punktzahl',
      'puzzle.moves': 'Bewegungen',
      'puzzle.time': 'Zeit',
      'puzzle.nextLevel': 'Nächstes Level',
      'puzzle.playAgain': 'Erneut spielen',
      'puzzle.gameStats': 'Spielstatistiken',
      'puzzle.currentScore': 'Aktuelle Punktzahl',
      'puzzle.efficiency': 'Effizienz',
      'puzzle.timeBonus': 'Zeitbonus',
      
      // Footer
      'footer.copyright': '© 2025-∞ KI-Spiele-Hub. Alle Rechte vorbehalten.',
      'footer.made': 'Hergestellt mit ❤️ und KI',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Erleben Sie die Zukunft des Spielens mit unserer KI-gestützten Puzzle-Sammlung. Fordern Sie Ihren Verstand mit futuristischer Cyber-Ästhetik und innovativem Gameplay heraus.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Einfach',
      'game.difficulty.medium': 'Mittel',
      'game.difficulty.hard': 'Schwer',
      
      // Mobile Language Selector
      'language.selector': 'Sprache:',
    }
  },
  es: {
    translation: {
      // Navigation
      'nav.home': 'Inicio',
      'nav.games': 'Juegos',
      'nav.about': 'Acerca de',
      'nav.contact': 'Contacto',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Experimenta el Futuro del Juego',
      'hero.description': 'Sumérgete en una colección de juegos de puzzle impulsados por IA con estética cyber de vanguardia',
      'hero.cta': 'Comenzar a Jugar',
      
      // Games
      'games.title': 'Colección de Juegos',
      'games.subtitle': 'Elige Tu Desafío',
      'games.memory': 'Matriz de Memoria',
      'games.puzzle': 'Puzzle Cyber',
      'games.logic': 'Compuertas Lógicas',
      'games.pattern': 'Maestro de Patrones',
      'games.speed': 'Prueba de Velocidad',
      
      // Game Descriptions
      'games.memory.desc': 'Pon a prueba tu memoria con patrones generados por IA',
      'games.puzzle.desc': 'Resuelve puzzles con temática cyber',
      'games.logic.desc': 'Domina los circuitos lógicos digitales',
      'games.pattern.desc': 'Descubre patrones ocultos',
      'games.speed.desc': 'Desafía tu tiempo de reacción',
      
      // Game UI
      'game.score': 'Puntuación',
      'game.level': 'Nivel',
      'game.time': 'Tiempo',
      'game.start': 'Comenzar',
      'game.pause': 'Pausa',
      'game.restart': 'Reiniciar',
      'game.next': 'Siguiente Nivel',
      
      // Game Page
      'game.notFound': 'Juego No Encontrado',
      'game.available': 'Juegos Disponibles',
      'game.backToHome': 'Volver a Inicio',
      'game.loading': 'Cargando',
      'game.game': 'Juego',
      
      // Memory Game
      'memory.reset': 'Reiniciar',
      'memory.title': 'Desafío Matriz de Memoria',
      'memory.instructions': '¡Encuentra pares de símbolos coincidentes. Recuerda sus posiciones y combínalos todos para avanzar al siguiente nivel!',
      'memory.startGame': 'Comenzar Juego',
      'memory.levelProgress': 'Progreso del Nivel',
      'memory.pairsFound': 'pares encontrados',
      
      // Puzzle Game
      'puzzle.reset': 'Reiniciar',
      'puzzle.title': 'Desafío Puzzle Cyber',
      'puzzle.instructions': '¡Arma los números del 1 al {count} en orden, con el espacio vacío en la esquina inferior derecha! Haz clic en los bloques adyacentes al espacio vacío para moverlos.',
      'puzzle.startGame': 'Comenzar Juego',
      'puzzle.completed': '¡Puzzle Completado!',
      'puzzle.score': 'Puntuación',
      'puzzle.moves': 'Movimientos',
      'puzzle.time': 'Tiempo',
      'puzzle.nextLevel': 'Siguiente Nivel',
      'puzzle.playAgain': 'Jugar de Nuevo',
      'puzzle.gameStats': 'Estadísticas del Juego',
      'puzzle.currentScore': 'Puntuación Actual',
      'puzzle.efficiency': 'Eficiencia',
      'puzzle.timeBonus': 'Bono de Tiempo',
      
      // Footer
      'footer.copyright': '© 2025-∞ Hub de Juegos IA. Todos los derechos reservados.',
      'footer.made': 'Hecho con ❤️ e IA',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Experimenta el futuro del juego con nuestra colección de puzzles impulsados por IA. Desafía tu mente con estética cyber de vanguardia y jugabilidad innovadora.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Fácil',
      'game.difficulty.medium': 'Medio',
      'game.difficulty.hard': 'Difícil',
      
      // Mobile Language Selector
      'language.selector': 'Idioma:',
    }
  },
  it: {
    translation: {
      // Navigation
      'nav.home': 'Home',
      'nav.games': 'Giochi',
      'nav.about': 'Chi Siamo',
      'nav.contact': 'Contatti',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Vivi il Futuro del Gioco',
      'hero.description': 'Immergiti in una collezione di giochi puzzle alimentati dall\'IA con estetica cyber all\'avanguardia',
      'hero.cta': 'Inizia a Giocare',
      
      // Games
      'games.title': 'Collezione di Giochi',
      'games.subtitle': 'Scegli la Tua Sfida',
      'games.memory': 'Matrice di Memoria',
      'games.puzzle': 'Puzzle Cyber',
      'games.logic': 'Porte Logiche',
      'games.pattern': 'Maestro dei Modelli',
      'games.speed': 'Test di Velocità',
      
      // Game Descriptions
      'games.memory.desc': 'Metti alla prova la tua memoria con modelli generati dall\'IA',
      'games.puzzle.desc': 'Risolvi puzzle a tema cyber',
      'games.logic.desc': 'Padroneggia i circuiti logici digitali',
      'games.pattern.desc': 'Scopri modelli nascosti',
      'games.speed.desc': 'Sfida il tuo tempo di reazione',
      
      // Game UI
      'game.score': 'Punteggio',
      'game.level': 'Livello',
      'game.time': 'Tempo',
      'game.start': 'Inizia',
      'game.pause': 'Pausa',
      'game.restart': 'Ricomincia',
      'game.next': 'Livello Successivo',
      
      // Game Page
      'game.notFound': 'Gioco Non Trovato',
      'game.available': 'Giochi Disponibili',
      'game.backToHome': 'Torna a Home',
      'game.loading': 'Caricamento',
      'game.game': 'Gioco',
      
      // Memory Game
      'memory.reset': 'Ricomincia',
      'memory.title': 'Sfida Matrice di Memoria',
      'memory.instructions': 'Trova coppie di simboli corrispondenti. Ricorda le loro posizioni e abbinali tutti per passare al livello successivo!',
      'memory.startGame': 'Inizia Gioco',
      'memory.levelProgress': 'Progresso Livello',
      'memory.pairsFound': 'coppie trovate',
      
      // Puzzle Game
      'puzzle.reset': 'Ricomincia',
      'puzzle.title': 'Sfida Puzzle Cyber',
      'puzzle.instructions': 'Arma i numeri da 1 a {count} nell\'ordine, con lo spazio vuoto nell\'angolo in basso a destra! Clicca sui blocchi adiacenti allo spazio vuoto per spostarli.',
      'puzzle.startGame': 'Inizia Gioco',
      'puzzle.completed': 'Puzzle Completato!',
      'puzzle.score': 'Punteggio',
      'puzzle.moves': 'Movimenti',
      'puzzle.time': 'Tempo',
      'puzzle.nextLevel': 'Livello Successivo',
      'puzzle.playAgain': 'Riprova',
      'puzzle.gameStats': 'Statistiche del Gioco',
      'puzzle.currentScore': 'Punteggio Attuale',
      'puzzle.efficiency': 'Efficacia',
      'puzzle.timeBonus': 'Bonus Tempo',
      
      // Footer
      'footer.copyright': '© 2025-∞ Hub di Giochi IA. Tutti i diritti riservati.',
      'footer.made': 'Fatto con ❤️ e IA',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Vivi il futuro del gioco con la nostra collezione di puzzle alimentati dall\'IA. Sfida la tua mente con un\'estetica cyber all\'avanguardia e un gameplay innovativo.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Facile',
      'game.difficulty.medium': 'Medio',
      'game.difficulty.hard': 'Difficile',
      
      // Mobile Language Selector
      'language.selector': 'Lingua:',
    }
  },
  pt: {
    translation: {
      // Navigation
      'nav.home': 'Início',
      'nav.games': 'Jogos',
      'nav.about': 'Sobre',
      'nav.contact': 'Contato',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Experimente o Futuro dos Jogos',
      'hero.description': 'Mergulhe em uma coleção de jogos de puzzle alimentados por IA com estética cyber de ponta',
      'hero.cta': 'Começar a Jogar',
      
      // Games
      'games.title': 'Coleção de Jogos',
      'games.subtitle': 'Escolha Seu Desafio',
      'games.memory': 'Matriz de Memória',
      'games.puzzle': 'Puzzle Cyber',
      'games.logic': 'Portas Lógicas',
      'games.pattern': 'Mestre de Padrões',
      'games.speed': 'Teste de Velocidade',
      
      // Game Descriptions
      'games.memory.desc': 'Teste sua memória com padrões gerados por IA',
      'games.puzzle.desc': 'Resolva puzzles com temática cyber',
      'games.logic.desc': 'Domine circuitos lógicos digitais',
      'games.pattern.desc': 'Descubra padrões ocultos',
      'games.speed.desc': 'Desafie seu tempo de reação',
      
      // Game UI
      'game.score': 'Pontuação',
      'game.level': 'Nível',
      'game.time': 'Tempo',
      'game.start': 'Começar',
      'game.pause': 'Pausa',
      'game.restart': 'Reiniciar',
      'game.next': 'Próximo Nível',
      
      // Game Page
      'game.notFound': 'Jogo Não Encontrado',
      'game.available': 'Jogos Disponíveis',
      'game.backToHome': 'Voltar para Início',
      'game.loading': 'Carregando',
      'game.game': 'Jogo',
      
      // Memory Game
      'memory.reset': 'Reiniciar',
      'memory.title': 'Desafio Matriz de Memória',
      'memory.instructions': 'Encontre pares de símbolos correspondentes. Lembre-se de suas posições e combine todos para avançar para o próximo nível!',
      'memory.startGame': 'Iniciar Jogo',
      'memory.levelProgress': 'Progresso do Nível',
      'memory.pairsFound': 'pares encontrados',
      
      // Puzzle Game
      'puzzle.reset': 'Reiniciar',
      'puzzle.title': 'Desafio Puzzle Cyber',
      'puzzle.instructions': 'Arma os números de 1 a {count} na ordem, com o espaço vazio na esquina inferior direita! Clique nos blocos adjacentes ao espaço vazio para movê-los.',
      'puzzle.startGame': 'Iniciar Jogo',
      'puzzle.completed': 'Puzzle Completado!',
      'puzzle.score': 'Pontuação',
      'puzzle.moves': 'Movimentos',
      'puzzle.time': 'Tempo',
      'puzzle.nextLevel': 'Próximo Nível',
      'puzzle.playAgain': 'Jogar Novamente',
      'puzzle.gameStats': 'Estatísticas do Jogo',
      'puzzle.currentScore': 'Pontuação Atual',
      'puzzle.efficiency': 'Eficiência',
      'puzzle.timeBonus': 'Bônus de Tempo',
      
      // Footer
      'footer.copyright': '© 2025-∞ Hub de Jogos IA. Todos os direitos reservados.',
      'footer.made': 'Feito com ❤️ e IA',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Experimente o futuro dos jogos com nossa coleção de puzzles alimentados por IA. Desafie sua mente com estética cyber de ponta e jogabilidade inovadora.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Fácil',
      'game.difficulty.medium': 'Médio',
      'game.difficulty.hard': 'Difícil',
      
      // Mobile Language Selector
      'language.selector': 'Idioma:',
    }
  },
  ru: {
    translation: {
      // Navigation
      'nav.home': 'Главная',
      'nav.games': 'Игры',
      'nav.about': 'О нас',
      'nav.contact': 'Контакты',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'Испытайте Будущее Игр',
      'hero.description': 'Погрузитесь в коллекцию головоломок на базе ИИ с передовой кибер-эстетикой',
      'hero.cta': 'Начать Игру',
      
      // Games
      'games.title': 'Коллекция Игр',
      'games.subtitle': 'Выберите Свой Вызов',
      'games.memory': 'Матрица Памяти',
      'games.puzzle': 'Кибер-Головоломка',
      'games.logic': 'Логические Ворота',
      'games.pattern': 'Мастер Паттернов',
      'games.speed': 'Тест Скорости',
      
      // Game Descriptions
      'games.memory.desc': 'Проверьте свою память с помощью паттернов, созданных ИИ',
      'games.puzzle.desc': 'Решите головоломки в кибер-стиле',
      'games.logic.desc': 'Освойте цифровые логические схемы',
      'games.pattern.desc': 'Откройте скрытые паттерны',
      'games.speed.desc': 'Испытайте время реакции',
      
      // Game UI
      'game.score': 'Счет',
      'game.level': 'Уровень',
      'game.time': 'Время',
      'game.start': 'Старт',
      'game.pause': 'Пауза',
      'game.restart': 'Перезапуск',
      'game.next': 'Следующий Уровень',
      
      // Game Page
      'game.notFound': 'Игра Не Найдена',
      'game.available': 'Игры Доступны',
      'game.backToHome': 'Вернуться на Главную',
      'game.loading': 'Загрузка',
      'game.game': 'Игра',
      
      // Memory Game
      'memory.reset': 'Сброс',
      'memory.title': 'Вызов Матрицы Памяти',
      'memory.instructions': 'Найдите соответствующие пары символов. Запомните их позиции и сопоставьте все, чтобы перейти на следующий уровень!',
      'memory.startGame': 'Начать Игру',
      'memory.levelProgress': 'Прогресс Уровня',
      'memory.pairsFound': 'пар найдено',
      
      // Puzzle Game
      'puzzle.reset': 'Сброс',
      'puzzle.title': 'Вызов Кибер-Головоломки',
      'puzzle.instructions': 'Расположите числа от 1 до {count} в правильном порядке, с пустым полем в нижнем правом углу. Нажмите на плитки, рядом с которыми находится пустое поле, чтобы переместить их.',
      'puzzle.startGame': 'Начать Игру',
      'puzzle.completed': 'Головоломка Завершена!',
      'puzzle.score': 'Счет',
      'puzzle.moves': 'Ходы',
      'puzzle.time': 'Время',
      'puzzle.nextLevel': 'Следующий Уровень',
      'puzzle.playAgain': 'Сыграть Снова',
      'puzzle.gameStats': 'Статистика Игры',
      'puzzle.currentScore': 'Текущий Счет',
      'puzzle.efficiency': 'Эффективность',
      'puzzle.timeBonus': 'Бонус Времени',
      
      // Footer
      'footer.copyright': '© 2025-∞ ИИ Игровой Хаб. Все права защищены.',
      'footer.made': 'Сделано с ❤️ и ИИ',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'Испытайте будущее игр с нашей коллекцией головоломок на базе ИИ. Бросьте вызов своему разуму с передовой кибер-эстетикой и инновационным геймплеем.',
      
      // Game Difficulty
      'game.difficulty.easy': 'Легко',
      'game.difficulty.medium': 'Средне',
      'game.difficulty.hard': 'Сложно',
      
      // Mobile Language Selector
      'language.selector': 'Язык:',
    }
  },
  ar: {
    translation: {
      // Navigation
      'nav.home': 'الرئيسية',
      'nav.games': 'الألعاب',
      'nav.about': 'حول',
      'nav.contact': 'اتصل بنا',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'اختبر مستقبل الألعاب',
      'hero.description': 'انغمس في مجموعة من ألعاب الألغاز المدعومة بالذكاء الاصطناعي مع جماليات سايبر متطورة',
      'hero.cta': 'ابدأ اللعب',
      
      // Games
      'games.title': 'مجموعة الألعاب',
      'games.subtitle': 'اختر تحديك',
      'games.memory': 'مصفوفة الذاكرة',
      'games.puzzle': 'لغز السايبر',
      'games.logic': 'بوابات المنطق',
      'games.pattern': 'سيد الأنماط',
      'games.speed': 'اختبار السرعة',
      
      // Game Descriptions
      'games.memory.desc': 'اختبر ذاكرتك مع أنماط مولدة بالذكاء الاصطناعي',
      'games.puzzle.desc': 'حل ألغاز بموضوع السايبر',
      'games.logic.desc': 'أتقن الدوائر المنطقية الرقمية',
      'games.pattern.desc': 'اكتشف الأنماط المخفية',
      'games.speed.desc': 'تحدى وقت رد فعلك',
      
      // Game UI
      'game.score': 'النقاط',
      'game.level': 'المستوى',
      'game.time': 'الوقت',
      'game.start': 'ابدأ',
      'game.pause': 'إيقاف مؤقت',
      'game.restart': 'إعادة تشغيل',
      'game.next': 'المستوى التالي',
      
      // Game Page
      'game.notFound': 'اللعبة غير موجودة',
      'game.available': 'الألعاب متاحة',
      'game.backToHome': 'العودة للصفحة الرئيسية',
      'game.loading': 'جاري التحميل',
      'game.game': 'اللعبة',
      
      // Memory Game
      'memory.reset': 'إعادة تعيين',
      'memory.title': 'تحدي مصفوفة الذاكرة',
      'memory.instructions': 'اعثر على أزواج من الرموز المتطابقة. تذكر مواقعها وطابقها جميعاً للانتقال إلى المستوى التالي!',
      'memory.startGame': 'ابدأ اللعبة',
      'memory.levelProgress': 'تقدم المستوى',
      'memory.pairsFound': 'زوج تم العثور عليه',
      
      // Puzzle Game
      'puzzle.reset': 'إعادة تعيين',
      'puzzle.title': 'تحدي لغز السايبر',
      'puzzle.instructions': 'ضع الأرقام من 1 إلى {count} بالترتيب، مع المساحة الفارغة في الزاوية اليمنى السفلية. اضغط على اللوحات المجاورة للمساحة الفارغة لنقلها.',
      'puzzle.startGame': 'ابدأ اللعبة',
      'puzzle.completed': 'لغز السايبر تم الانتهاء!',
      'puzzle.score': 'النقاط',
      'puzzle.moves': 'الحركات',
      'puzzle.time': 'الوقت',
      'puzzle.nextLevel': 'المستوى التالي',
      'puzzle.playAgain': 'العودة لللعبة مرة أخرى',
      'puzzle.gameStats': 'إحصائيات اللعبة',
      'puzzle.currentScore': 'النقاط الحالية',
      'puzzle.efficiency': 'الكفاءة',
      'puzzle.timeBonus': 'مكافأة الوقت',
      
      // Footer
      'footer.copyright': '© 2025-∞ مركز ألعاب الذكاء الاصطناعي. جميع الحقوق محفوظة.',
      'footer.made': 'صنع بـ ❤️ والذكاء الاصطناعي',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'اختبر مستقبل الألعاب مع مجموعتنا من الألغاز المدعومة بالذكاء الاصطناعي. تحدى عقلك مع جماليات سايبر متطورة ولعب مبتكر.',
      
      // Game Difficulty
      'game.difficulty.easy': 'سهل',
      'game.difficulty.medium': 'متوسط',
      'game.difficulty.hard': 'صعب',
      
      // Mobile Language Selector
      'language.selector': 'اللغة:',
    }
  },
  hi: {
    translation: {
      // Navigation
      'nav.home': 'होम',
      'nav.games': 'खेल',
      'nav.about': 'हमारे बारे में',
      'nav.contact': 'संपर्क',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'गेमिंग का भविष्य अनुभव करें',
      'hero.description': 'कटिंग-एज साइबर एस्थेटिक्स के साथ एआई-संचालित पज़ल गेम्स के संग्रह में डूब जाएं',
      'hero.cta': 'खेलना शुरू करें',
      
      // Games
      'games.title': 'गेम संग्रह',
      'games.subtitle': 'अपनी चुनौती चुनें',
      'games.memory': 'मेमोरी मैट्रिक्स',
      'games.puzzle': 'साइबर पज़ल',
      'games.logic': 'लॉजिक गेट्स',
      'games.pattern': 'पैटर्न मास्टर',
      'games.speed': 'स्पीड टेस्ट',
      
      // Game Descriptions
      'games.memory.desc': 'एआई-जनरेटेड पैटर्न के साथ अपनी याददाश्त का परीक्षण करें',
      'games.puzzle.desc': 'साइबर-थीम वाले पज़ल्स को हल करें',
      'games.logic.desc': 'डिजिटल लॉजिक सर्किट में महारत हासिल करें',
      'games.pattern.desc': 'छिपे हुए पैटर्न खोजें',
      'games.speed.desc': 'अपने प्रतिक्रिया समय को चुनौती दें',
      
      // Game UI
      'game.score': 'स्कोर',
      'game.level': 'स्तर',
      'game.time': 'समय',
      'game.start': 'शुरू करें',
      'game.pause': 'रोकें',
      'game.restart': 'पुनः प्रारंभ करें',
      'game.next': 'अगला स्तर',
      
      // Game Page
      'game.notFound': 'गेम नहीं मिला',
      'game.available': 'उपलब्ध गेम',
      'game.backToHome': 'होम पर वापस जाएं',
      'game.loading': 'लोडिंग हो रही है',
      'game.game': 'गेम',
      
      // Memory Game
      'memory.reset': 'रीसेट',
      'memory.title': 'मेमोरी मैट्रिक्स चुनौती',
      'memory.instructions': 'मिलान वाले प्रतीकों के जोड़े खोजें। उनकी स्थिति याद रखें और सभी को मिलाएं ताकि अगले स्तर पर जा सकें!',
      'memory.startGame': 'गेम शुरू करें',
      'memory.levelProgress': 'स्तर प्रगति',
      'memory.pairsFound': 'जोड़े मिले',
      
      // Puzzle Game
      'puzzle.reset': 'रीसेट',
      'puzzle.title': 'साइबर पज़ल चुनौती',
      'puzzle.instructions': '1 से {count} तक के संख्याओं को 1 से {count} तक व्यवस्थित करें, जहां खाली स्थान नीचे की दाहिनी ओर है। खाली स्थान के आसपास की टाइल्स पर क्लिक करके उन्हें चलाएं।',
      'puzzle.startGame': 'गेम शुरू करें',
      'puzzle.completed': 'साइबर पज़ल पूर्ण हो गया!',
      'puzzle.score': 'स्कोर',
      'puzzle.moves': 'चाल',
      'puzzle.time': 'समय',
      'puzzle.nextLevel': 'अगला स्तर',
      'puzzle.playAgain': 'फिर खेलें',
      'puzzle.gameStats': 'गेम स्टेटिस्टिक्स',
      'puzzle.currentScore': 'वर्तमान स्कोर',
      'puzzle.efficiency': 'कुशलता',
      'puzzle.timeBonus': 'समय बोनस',
      
      // Footer
      'footer.copyright': '© 2025-∞ एआई गेम्स हब। सर्वाधिकार सुरक्षित।',
      'footer.made': '❤️ और एआई के साथ बनाया गया',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'हमारे AI-संचालित पज़ल संग्रह के साथ गेमिंग का भविष्य अनुभव करें। कटिंग-एज साइबर एस्थेटिक्स और नवीन गेमप्ले के साथ अपने दिमाग को चुनौती दें।',
      
      // Game Difficulty
      'game.difficulty.easy': 'आसान',
      'game.difficulty.medium': 'मध्यम',
      'game.difficulty.hard': 'कठिन',
      
      // Mobile Language Selector
      'language.selector': 'भाषा:',
    }
  },
  ja: {
    translation: {
      // Navigation
      'nav.home': 'ホーム',
      'nav.games': 'ゲーム',
      'nav.about': 'について',
      'nav.contact': 'お問い合わせ',
      
      // Hero Section
      'hero.title': 'Games Hub',
      'hero.subtitle': 'ゲームの未来を体験',
      'hero.description': 'AI駆動のパズルゲームコレクションで、最先端のサイバー美学を体験',
      'hero.cta': 'ゲーム開始',
      
      // Games
      'games.title': 'ゲームコレクション',
      'games.subtitle': 'チャレンジを選択',
      'games.memory': 'メモリーマトリックス',
      'games.puzzle': 'サイバーパズル',
      'games.logic': 'ロジックゲート',
      'games.pattern': 'パターンマスター',
      'games.speed': 'スピードテスト',
      'games.dino': 'Chrome恐竜ランナー',
      'games.snake': 'サイバースネーク',
      'games.tetris': 'サイバーテトリス',
      'games.game2048': '2048',
      'games.colormatch': 'カラーマッチ',
      'games.wordsearch': 'ワードサーチ',
      'games.math': '数学チャレンジ',
      'games.reaction': '反応テスト',
      'games.rhythm': 'リズムマスター',
      'games.maze': 'サイバーメイズ',
      
      // Game Descriptions
      'games.memory.desc': 'AI生成パターンで記憶力をテスト',
      'games.puzzle.desc': 'サイバーテーマのパズルを解決',
      'games.logic.desc': 'デジタルロジック回路をマスター',
      'games.pattern.desc': '隠されたパターンを発見',
      'games.speed.desc': '反応時間に挑戦',
      'games.dino.desc': 'サイバー美学を持つクラシックオフライン恐竜ゲーム',
      'games.snake.desc': 'ネオン視覚効果と成長するチャレンジを持つクラシックスネークゲーム',
      'games.tetris.desc': 'ネオンブロックと増加する速度を持つ未来のテトリス',
      'games.game2048.desc': 'サイバースタイリングを持つ中毒性のある数字パズルゲーム',
      'games.colormatch.desc': '色認識とマッチングスキルをテスト',
      'games.wordsearch.desc': 'サイバーテーマのグリッドで隠された単語を見つける',
      'games.math.desc': '数学の問題を解決し、ボーナスポイントのために連続記録を構築',
      'games.reaction.desc': '反射神経と反応時間をテスト',
      'games.rhythm.desc': 'ビートに合わせてリズムでノートをクリックし、コンボを構築',
      'games.maze.desc': 'プログラム生成された迷路をナビゲート',
      
      // Game UI
      'game.score': 'スコア',
      'game.level': 'レベル',
      'game.time': '時間',
      'game.start': '開始',
      'game.pause': '一時停止',
      'game.restart': '再開',
      'game.next': '次のレベル',
      
      // Game Page
      'game.notFound': 'ゲームが見つかりません',
      'game.available': '利用可能なゲーム',
      'game.backToHome': 'ホームに戻る',
      'game.loading': 'ローディング中',
      'game.game': 'ゲーム',
      
      // Memory Game
      'memory.reset': 'リセット',
      'memory.title': 'メモリーマトリックスチャレンジ',
      'memory.instructions': '一致するシンボルのペアを見つけてください。位置を覚えて、すべてを一致させて次のレベルに進みましょう！',
      'memory.startGame': 'ゲーム開始',
      'memory.levelProgress': 'レベル進行',
      'memory.pairsFound': 'ペアが見つかりました',
      
      // Puzzle Game
      'puzzle.reset': 'リセット',
      'puzzle.title': 'サイバーパズルチャレンジ',
      'puzzle.instructions': '1から{count}までの数字を1から{count}までの順序で並べ、右下の空きスペースを含めます。空きスペースの隣のタイルをクリックして移動させます。',
      'puzzle.startGame': 'ゲーム開始',
      'puzzle.completed': 'サイバーパズル完了！',
      'puzzle.score': 'スコア',
      'puzzle.moves': '移動',
      'puzzle.time': '時間',
      'puzzle.nextLevel': '次のレベル',
      'puzzle.playAgain': 'もう一度プレイ',
      'puzzle.gameStats': 'ゲーム統計',
      'puzzle.currentScore': '現在のスコア',
      'puzzle.efficiency': '効率',
      'puzzle.timeBonus': 'タイムボーナス',
      
      // Footer
      'footer.copyright': '© 2025-∞ AIゲームハブ。全権利を保留。',
      'footer.made': '❤️ とAIで制作',
      
      // Language
      'language.en': 'English',
      'language.zh': '中文',
      'language.ja': '日本語',
      'language.ko': '한국어',
      'language.fr': 'Français',
      'language.de': 'Deutsch',
      'language.es': 'Español',
      'language.it': 'Italiano',
      'language.pt': 'Português',
      'language.ru': 'Русский',
      'language.ar': 'العربية',
      'language.hi': 'हिन्दी',
      
      // Footer Description
      'footer.description': 'AI駆動のパズルゲームコレクションでゲームの未来を体験。最先端のサイバー美学と革新的なゲームプレイで心を挑戦。',
      
      // Game Difficulty
      'game.difficulty.easy': '簡単',
      'game.difficulty.medium': '普通',
      'game.difficulty.hard': '困難',
      
      // Mobile Language Selector
      'language.selector': '言語:',
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: 'en', // 强制设置初始语言为英文
    debug: false,
    
    interpolation: {
      escapeValue: false,
    },
    
    detection: {
      order: ['localStorage', 'querystring'],
      caches: ['localStorage'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
      checkWhitelist: true,
    },
  });

export default i18n;
